import { z } from 'zod';
import { getCurrentFiscalYear } from '@/shared/lib/fiscal-year';
import { DEFAULT_SEARCH, PageSize } from '@/shared/lib/route';

/**
 * 財源詳細（財源別事業一覧）ページの URL 検索パラメータ。
 *
 * - year: 事業年度。一覧ページから引き継ぐ。詳細は一覧と同じ year で参照マッチを取得しないと
 *   クリックした財源（record）が別年度に存在せずリダイレクトされるため、year を URL で受け渡す。
 * - 事業一覧と同一のマスタ連携フィルター（project_name / budget_management_division_ids /
 *   euc_import_master_id / tag_ids / accounting_type_id / kan_ids / ko_ids / moku_ids）を、
 *   取得済みのマッチ事業に対してクライアント側で適用するため URL に保持する。
 * - sort: 並び替え（`<field>:<direction>` 配列。寛容に string[] で受ける）。
 * - amount_unit: 予算額の表示単位（thousand=千円 / 未指定=円）。
 * - page / page_size: クライアント側ページネーション。
 * - project_id: 右サイドパネルで開いている事業の ID。URL に保持することで、リロード・URL 共有時に
 *   同じ事業のサイドパネルを復元する（ITA-2746）。
 *
 * 旧 URL / 不正値でルートロードが失敗しないよう各フィールドは `.catch()` で寛容にする
 * （`/fe-router-pitfalls` 準拠）。
 */
export const fundingDetailSearchSchema = z.object({
  year: z.number().int().min(1900).max(2100).catch(getCurrentFiscalYear()).optional(),
  project_name: z.string().catch('').optional(),
  budget_management_division_ids: z.array(z.string()).catch([]).optional(),
  euc_import_master_id: z.string().catch('').optional(),
  tag_ids: z.array(z.string()).catch([]).optional(),
  accounting_type_id: z.string().catch('').optional(),
  kan_ids: z.array(z.string()).catch([]).optional(),
  ko_ids: z.array(z.string()).catch([]).optional(),
  moku_ids: z.array(z.string()).catch([]).optional(),
  sort: z.array(z.string()).catch([]).optional(),
  amount_unit: z.enum(['yen', 'thousand']).catch('yen').optional(),
  page: z.number().int().min(1).default(DEFAULT_SEARCH.page).optional(),
  page_size: PageSize.default(DEFAULT_SEARCH.pageSize).optional(),
  project_id: z.string().catch('').optional(),
});

export type FundingDetailSearch = z.infer<typeof fundingDetailSearchSchema>;
